import { faSearch } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
    forwardRef as reactForwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { Container, Form, InputGroup, Row } from "react-bootstrap";
import "./search.scss";

import { debounce } from "lodash";
import { t } from "i18next";

/**
* @typedef {'fund' | 'index' | 'equity' | undefined} SearchType
*/

/**
 * Search component
 * @param {Object} props - Component props
 * @param {Function} props.selectedSearchItem - Callback when an item is selected
 * @param {boolean} [props.disabled] - Whether the search is disabled
 * @param {string} [props.filterByClass] - Filter by class
 * @param {SearchType} [props.type] - Filter by entity type (fund, index, or equity)
 * @param {Object} ref - Forwarded ref
 */

const Search = reactForwardRef((props, ref) => {

    const inputRef = useRef();
    const [searchValue, setSearchValue] = useState("");
    const [searchResult, setSearchResult] = useState([]);
    const [searchResultContainer, setSearchResultContainer] = useState(false);
    const [isEmptyResult, setIsEmptyResult] = useState(false);
    const searchResultsRef = useRef([]);
    const searchResultContainerRef = useRef(null);
    const searchResultRowsRef = useRef(null);

    const [selectedResultIndex, setSelectedResultIndex] = useState(-1); // Seçili sonuç için yeni state

    useImperativeHandle(ref, () => ({
        focus: () => {
            inputRef.current.focus();
            if (props.lastKeyPressed && props.lastKeyPressed.current !== null) {
                inputRef.current.value = props.lastKeyPressed.current;
                props.lastKeyPressed.current = null; // Son tuşu sıfırla
                const event = new Event('input', { bubbles: true });
                inputRef.current.dispatchEvent(event);
            }
        }
    }));

    useEffect(() => {
        if (searchResult.length > 0) {
            setSelectedResultIndex(0);
        } else {
            setSelectedResultIndex(-1);
        }
    }, [searchResult]);

    useEffect(() => {
        // Update searchValue whether inputValue exists or not
        // This ensures the search field is cleared when inputValue becomes empty
        setSearchValue(props.inputValue || "");
    }, [props.inputValue]);


    const handleKeyDown = useCallback(
        (event) => {
            let nextIndex;
            if (event.key === "ArrowDown") {
                if (selectedResultIndex >= searchResult.length - 1) {
                    nextIndex = 0;
                } else {
                    nextIndex = selectedResultIndex + 1;
                }
                setSelectedResultIndex(nextIndex);
                adjustScroll(nextIndex);
                event.preventDefault();
            } else if (event.key === "ArrowUp") {
                if (selectedResultIndex <= 0) {
                    nextIndex = searchResult.length - 1;
                } else {
                    nextIndex = selectedResultIndex - 1;
                }
                setSelectedResultIndex(nextIndex);
                adjustScroll(nextIndex);
                event.preventDefault();
            } else if (event.key === "Enter") {
                if (selectedResultIndex !== -1 && searchResult[selectedResultIndex]) {
                    // Seçili satır için `handleRowClick` fonksiyonunu çağır
                    handleRowClick(searchResult[selectedResultIndex]);
                    event.preventDefault(); // Form gönderimini önle
                }
            }
        }, [searchResult.length, selectedResultIndex]);

    const adjustScroll = (index) => {
        const currentItem = searchResultsRef.current[index];
        if (currentItem && searchResultContainerRef.current) {
            const container = searchResultContainerRef.current;
            const containerResultRows = searchResultRowsRef.current;
            const containerTop = container.scrollTop;
            const containerBottom = containerTop + container.clientHeight;
            const elementTop = currentItem.offsetTop;
            const elementBottom = elementTop + currentItem.clientHeight;

            if (elementBottom > containerBottom) {
                // Seçili öğe görünüm alanının altında kalıyorsa, öğeyi görünür yapmak için aşağı doğru scroll yap
                containerResultRows.scrollTop = elementBottom - container.clientHeight;
            } else if (elementTop < containerTop) {
                // Seçili öğe görünüm alanının üstünde kalıyorsa, öğeyi görünür yapmak için yukarı doğru scroll yap
                containerResultRows.scrollTop = elementTop;
            }
        }
    };

    useEffect(() => {
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [handleKeyDown]);

    // useEffect(() => {
    //   getPopularSearch();
    // }, []);

    // const getPopularSearch = () => {
    //   setSearchPopularResult(dummyPopularSearchArray);
    // };

    const fetchSearchResults = useCallback(
        debounce((query) => {
            if (!query) {
                setSearchResult([]);
                return;
            }
            console.log(query);

            setIsEmptyResult(false);

            // Build filter conditions
            let filterConditions = ['visible:true'];

            // Add class filter if provided
            if (props.filterByClass) {
                filterConditions.push(`class:${props.filterByClass}`);
            }

            // Add type filter if provided
            if (props.type) {
                switch (props.type) {
                    case 'fund':
                        filterConditions.push('class:Fund');
                        break;
                    case 'index':
                        filterConditions.push('index:true');
                        break;
                    case 'equity':
                        filterConditions.push('class:Equity');
                        break;
                    // Default case - no additional filter
                }
            }

            // Combine all filters with &&
            const filterString = filterConditions.join('&&');

            // const url =
            //     'https://api.rasyonet.com/service-hisselab-spotlight/collections/Hisselab.Symbols/documents/search'
            //     + `?q=${encodeURIComponent(query)}`
            //     + '&query_by=name,code'
            //     + '&listed:true'
            //     + `&filter_by=${encodeURIComponent(filterString)}`
            const url = `https://api.rasyonet.com/service-hisselab-spotlight/collections/Hisselab.Symbols/documents/search?q=${query}&query_by=name,code&filter_by=visible:true&&listed:true&&class:Equity`;


            fetch(url, {
                method: "GET",
                headers: {
                    "x-typesense-api-key": "masterKeyk8pX5hD0793d8YQC5aD1aEPd7VleSuGP",
                },
            })
                .then((response) => response.json())
                .then((data) => {
                    console.log("fetchSearchResults:", data);
                    setSearchResultContainer(true);

                    if (data.hits == undefined || data.hits.length == 0) {
                        setSearchResult([]);
                        setIsEmptyResult(true);
                        return;
                    }
                    setSearchResult(data.hits);
                })
                .catch((error) => {
                    console.error("fetchSearchResults Error:", error);
                    if (document.getElementById("dataContainer")) {
                        document.getElementById("dataContainer").innerHTML =
                            "Bir hata oluştu: " + error;
                    }
                });
        }, 750),
        [props.filterByClass, props.type] // Add props.type to dependencies
    );

    const handleChange = (event) => {
        const value = event.target.value;
        setSearchValue(value);
        // setSelectedResultIndex(-1);

        if (value.length >= 3) {
            console.log("Uzunluk 3.");
            fetchSearchResults(value);
        } else if (value.length === 0) {
            setSearchResultContainer(false);
        }
    };

    const handleFocus = () => {
        try {
            // setSearchResultContainer(true);
        } catch (error) {
            console.log(error);
        }
    };


    const handleBlur = () => {
        const timeout = setTimeout(() => {
            setSearchResultContainer(false);
        }, 300);
        // setBlurTimeout(timeout);
    };

    const handleRowClick = (item) => {
        props.selectedSearchItem(item);
        setSearchValue(item.document.code);
        setSearchResultContainer(false);
    };

    const resultItemClass = (index) =>
        `resault-wrap ${index === selectedResultIndex ? "hovered" : ""}`;

    return (
        <div className="position-relative search-component">
            <InputGroup className="main-search-input-group mx-auto">
                <InputGroup.Text id="inputGroup-sizing-default">
                    <FontAwesomeIcon icon={faSearch} className="icon" />
                </InputGroup.Text>
                <Form.Control
                    className="main-search-input"
                    aria-label="Default"
                    aria-describedby="inputGroup-sizing-default"
                    placeholder={t("seachSymbolShare")}
                    value={searchValue}
                    onChange={(e) => handleChange(e)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    ref={inputRef}
                    disabled={props.disabled ? props.disabled : false}
                />
            </InputGroup>
            {searchResultContainer && (
                <Container
                    ref={searchResultContainerRef}
                    className="searchResultContainer position-absolute"
                >

                    <Row ref={searchResultRowsRef} className="searchResulRow">
                        {searchResult.length > 0 &&
                            searchResult
                                // Filter results based on type prop
                                .filter(item => {
                                    // If no type is specified, show all results
                                    if (!props.type) {
                                        return true;
                                    }

                                    // Handle combined equity+index case
                                    if (props.type === 'equity') {
                                        // For equity type, show both regular equities and equity indices
                                        if (item.document.class === 'Equity') {
                                            return true;
                                        }
                                    }

                                    if (props.type === 'index') {
                                        // For index type, only show items with index=true
                                        if (item.document.index === true) {
                                            return true;
                                        }
                                    }

                                    if (props.type === 'fund') {
                                        // For fund type, only show items with class=Fund
                                        if (item.document.class === 'Fund') {
                                            return true;
                                        }
                                    }

                                    // No match found
                                    return false;
                                })
                                .map((item, index) => (
                                    <div
                                        ref={(el) => (searchResultsRef.current[index] = el)}
                                        key={item.document.id + index}
                                        className={resultItemClass(index)}
                                        onClick={() => handleRowClick(item)}
                                    >
                                        <div className="sec-detail">
                                            <div className="roboto-medium-12 ">
                                                {item.document.name}{" "}
                                            </div>
                                            <div className="roboto-regular-12 symbol-text">
                                                {item.document.code}{" "}
                                            </div>
                                        </div>
                                        <div className="sec-source">
                                            <div className="roboto-regular-10 source-text">
                                                Borsa Istanbul
                                            </div>
                                        </div>
                                    </div>
                                ))}
                        {isEmptyResult && (
                            <div
                                style={{
                                    padding: 25,
                                    display: "flex",
                                    height: "100%",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    flex: 1,
                                }}
                            >
                                <div className="roboto-medium-14 " style={{}}>
                                    Sonuç Bulunamadı{" "}
                                </div>
                            </div>
                        )}
                    </Row>
                </Container>
            )}
        </div>
    );
});
Search.displayName = 'Search';

export default Search;
